<script setup lang="ts">
import { computed, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import BrowseLayout from '@/browse/BrowseLayout.vue'
import ActiveFilters from '@/browse/ActiveFilters.vue'
import BrowseAssessmentsFilters from '@/browse/assessments/BrowseAssessmentsFilters.vue'
import BrowseAssessmentsResults from '@/browse/assessments/BrowseAssessmentsResults.vue'
import AssessmentWizardBanner from '@/browse/assessments/AssessmentWizardBanner.vue'
import AssessmentWizardQuerySummary from '@/browse/assessments/AssessmentWizardQuerySummary.vue'
import AssessmentWizardResultsStatus from '@/browse/assessments/AssessmentWizardResultsStatus.vue'
import { useBrowseSearch } from '@/browse/useBrowseSearch'
import { focusFilterControlWhenNoChipsRemain } from '@/browse/only_chip_focus'
import { KEYWORD_KEY, refine as refinedState, removeChip as stateWithoutChip, toggle as toggledState } from '@/browse/browse_state'
import type { BrowseState, Chip, ChipRow } from '@/browse/browse_state'
import { GRADES } from '@/browse/grades'
import {
  MAX_ASSESSMENT_RESULTS,
  NON_PREMIUM_RESULT_LIMIT,
  assessmentBrowseConfig,
  assessmentChipRemoveButtonName,
  assessmentsIndexFor,
  selectedSubjectOf
} from '@/browse/assessments/assessment_browse'
import type { AssessmentHit } from '@/browse/assessments/assessment_results'
import { getBrowseAssessmentsClient } from '@/search/algolia_search'
import { useAgenticSearchStore } from '@/search/agentic_search.store'
import type { AgentContent } from '@/search/agentic_search.store'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { betaOptIn } from '@/util/beta_opt_in'

const MAX_EVENT_KEYWORD_LENGTH = 120
const EMPTY_STATE_TIPS = [
  'Select a subject from the dropdown',
  'Filter by grade levels',
  'Choose a category for the subject selected above',
  'Search using keywords',
  'Filter by assessment type',
  'Filter by language'
]
const FROM_WIZARD_REF = 'wizard'

interface RemovedFilterEvent {
  eventName: string
  payloadKey: string
}

const REMOVED_FILTER_EVENTS: Record<string, RemovedFilterEvent> = {
  subject: { eventName: EVENT_NAMES.BROWSE_ASSESSMENTS.REMOVED_SUBJECT, payloadKey: 'browse_assessments_removed_subject' },
  category: { eventName: EVENT_NAMES.BROWSE_ASSESSMENTS.REMOVED_CATEGORY, payloadKey: 'browse_assessments_removed_category' },
  grades: { eventName: EVENT_NAMES.BROWSE_ASSESSMENTS.REMOVED_GRADE, payloadKey: 'browse_assessments_removed_grade' },
  type: { eventName: EVENT_NAMES.BROWSE_ASSESSMENTS.REMOVED_TYPE, payloadKey: 'browse_assessments_removed_type' },
  language: { eventName: EVENT_NAMES.BROWSE_ASSESSMENTS.REMOVED_LANGUAGE, payloadKey: 'browse_assessments_removed_language' },
  [KEYWORD_KEY]: { eventName: EVENT_NAMES.BROWSE_ASSESSMENTS.REMOVED_KEYWORD, payloadKey: 'browse_assessments_removed_keyword' }
}

const route = useRoute()
const router = useRouter()
const { fetching: isFetchingUser, isPremium } = storeToRefs(useUserStore())
const { aiFeatureVisibility } = storeToRefs(useVisibilityStore())
const agenticSearch = useAgenticSearchStore()
const { assessmentResults: wizardResults, hasAssessmentResults: hasWizardResults, latestSearch } = storeToRefs(agenticSearch)

const isPremiumUser = computed(() => Boolean(isPremium.value))

const config = assessmentBrowseConfig({
  isPremiumUser: () => isPremiumUser.value,
  isReadyForSearch: () => !isFetchingUser.value
})

const { state, hits, totalHits, queryID, facetCounts, isLoading, hasFailed, chipRows, chipRowsAfterRemoving, refine, toggle, removeChip, setKeyword } =
  useBrowseSearch<AssessmentHit>(config, getBrowseAssessmentsClient)

// Where this visit came from. A filter change drops ref from the URL without changing it, so a
// visit from the wizard is not offered the wizard again; a navigation bringing a ref of its own,
// such as the navbar link, starts a new visit.
const visitRef = ref(route.query.ref)
watch(() => route.query.ref, (arrivingRef) => {
  if (arrivingRef) {
    visitRef.value = arrivingRef
  }
})
const isFromWizard = computed(() => visitRef.value === FROM_WIZARD_REF)
const showsWizardBanner = computed(() => !isFromWizard.value && aiFeatureVisibility.value)
const hasWizardSearch = computed(() => Boolean(latestSearch.value.prompt))
const hasCriteria = computed(() => Boolean(selectedSubjectOf(state.value) || state.value.keyword))
const algoliaIndex = computed(() => assessmentsIndexFor(state.value))

// The wizard's results are indexed assessments, so they carry an Algolia hit's fields.
function asAssessmentHits(results: AgentContent[]): AssessmentHit[] {
  return results as unknown as AssessmentHit[]
}

// Non-premium users see a preview of the wizard's results, as they do of Algolia's.
const shownWizardResults = computed(() => {
  if (isPremiumUser.value) {
    return asAssessmentHits(wizardResults.value)
  }
  return asAssessmentHits(wizardResults.value.slice(0, NON_PREMIUM_RESULT_LIMIT))
})

function nonPremiumResultCountText(): string {
  const countHiddenWithoutPremium = totalHits.value - NON_PREMIUM_RESULT_LIMIT
  if (countHiddenWithoutPremium > 1) {
    return `${NON_PREMIUM_RESULT_LIMIT} results. (${countHiddenWithoutPremium} more with premium)`
  }
  return `${totalHits.value} results`
}

const resultCountText = computed(() => {
  if (!isPremiumUser.value) {
    return nonPremiumResultCountText()
  }
  if (totalHits.value < MAX_ASSESSMENT_RESULTS) {
    return `Found ${totalHits.value} results`
  }
  return `Found more than ${MAX_ASSESSMENT_RESULTS} results`
})

const announcedResultCountText = computed(() => (hasCriteria.value && !isLoading.value && !hasFailed.value ? resultCountText.value : ''))

function gradeOrder(gradeChip: Chip): number {
  return GRADES.indexOf(gradeChip.value)
}

function withGradeChipsInGradeOrder(row: ChipRow): ChipRow {
  const isGradesRow = row.chips.every((chip) => chip.key === 'grades')
  if (!isGradesRow) {
    return row
  }
  const gradeChipsInGradeOrder = [...row.chips].sort((gradeChipA, gradeChipB) => gradeOrder(gradeChipA) - gradeOrder(gradeChipB))
  return { ...row, chips: gradeChipsInGradeOrder }
}

const sortedChipRows = computed(() => chipRows.value.map(withGradeChipsInGradeOrder))
// Vue 2 draws the chips' bottom rule even with no chips, so the wizard summary always sits below a rule.
const showsRuleWithoutChips = computed(() => hasWizardSearch.value && sortedChipRows.value.every((row) => row.chips.length === 0))

// The events recorded while a filter changes carry the type the change leaves, so analytics
// attributes them to the search the change produces.
const stateBeingApplied = shallowRef<BrowseState>()
const selectedType = computed(() => (stateBeingApplied.value ?? state.value).refinements.type[0])

const analytics = getAnalyticsTracker().getChildContext({
  browse_assessments_selected_type: selectedType,
  wizard_results: hasWizardResults
})

// The event's database field holds only so many characters of a keyword.
function eventValueFor(key: string, value: string): string {
  if (key !== KEYWORD_KEY) {
    return value
  }
  return value.slice(0, MAX_EVENT_KEYWORD_LENGTH)
}

function trackRemovedFilter(key: string, value: string) {
  const { eventName, payloadKey } = REMOVED_FILTER_EVENTS[key]
  analytics.sendEvent(eventName, { [payloadKey]: eventValueFor(key, value) })
}

// The results list mounts with the first results for a subject or keyword and stays through
// later searches; Returned Browse Assessments Results is recorded on that lifetime.
const isAlgoliaResultListMounted = ref(false)
watch([hasCriteria, isLoading], () => {
  if (!hasCriteria.value) {
    isAlgoliaResultListMounted.value = false
    return
  }
  if (!isLoading.value) {
    isAlgoliaResultListMounted.value = true
  }
}, { immediate: true })
const isShowingAlgoliaResults = computed(() => !isLoading.value && !hasFailed.value)

const shownResults = useTemplateRef<InstanceType<typeof BrowseAssessmentsResults>>('shownResults')

// The results on show are recorded once more just before each filter change.
function recordShownResults() {
  shownResults.value?.recordReturnedResults()
}

// Normalised as the page's state is once the change reaches the URL, so a type left without
// the subject it needs reads as cleared.
async function changeFilters(stateAfterChange: BrowseState, applyChange: () => Promise<unknown>) {
  const pendingState = config.normalize?.(stateAfterChange) ?? stateAfterChange
  stateBeingApplied.value = pendingState
  try {
    recordShownResults()
    await applyChange()
  } finally {
    if (stateBeingApplied.value === pendingState) {
      stateBeingApplied.value = undefined
    }
  }
}

function refineFilter(key: string, values: string[]) {
  return changeFilters(refinedState(config, state.value, key, values), () => refine(key, values))
}

function toggleFilter(key: string, value: string) {
  return changeFilters(toggledState(config, state.value, key, value), () => toggle(key, value))
}

function searchKeyword(keyword: string) {
  recordShownResults()
  setKeyword(keyword)
}

function removeFilter(key: string, value: string) {
  return changeFilters(stateWithoutChip(config, state.value, key, value), () => {
    trackRemovedFilter(key, value)
    focusFilterControlWhenNoChipsRemain(chipRowsAfterRemoving(key, value))
    return removeChip(key, value)
  })
}

// Navigating within the app keeps the latest search in the store, which the wizard reads back
// to let the user refine it.
function refineWizardSearch() {
  analytics.sendEvent(EVENT_NAMES.BROWSE_ASSESSMENTS.CLICKED_REFINE_SEARCH)
  agenticSearch.clearResults()
  router.push({ name: 'AssessmentWizard', query: { ...betaOptIn(route.query), ref: FROM_WIZARD_REF } })
}

// The wizard's results belong to the visit that came from it; changing a filter replaces them.
const filtersFingerprint = computed(() => JSON.stringify(state.value))
watch(filtersFingerprint, () => agenticSearch.clearSearchAndResults())

if (!isFromWizard.value) {
  agenticSearch.clearSearchAndResults()
}

analytics.sendEvent(EVENT_NAMES.BROWSE_ASSESSMENTS.VIEWED_PAGE, { ref: route.query.ref })
</script>

<template>
  <BrowseLayout
    title="Browse Assessments"
    description="Discover targeted baseline and progress monitoring assessments from thousands of resources in our collection."
  >
    <template v-if="showsWizardBanner" #banner>
      <AssessmentWizardBanner />
    </template>

    <template v-if="!isFetchingUser" #filters>
      <BrowseAssessmentsFilters
        :state="state"
        :facet-counts="facetCounts"
        :is-searching="isLoading"
        @refine="refineFilter"
        @toggle="toggleFilter"
        @keyword="searchKeyword"
      />
    </template>

    <ActiveFilters :rows="sortedChipRows" :remove-button-name="assessmentChipRemoveButtonName" @remove="removeFilter" />
    <div v-if="showsRuleWithoutChips" data-testid="rule-without-chips" class="mb-3 border-b-2 border-gray-300" />
    <AssessmentWizardQuerySummary @refine-search="refineWizardSearch" />
    <AssessmentWizardResultsStatus :shown-count="shownWizardResults.length" />

    <BrowseAssessmentsResults
      v-if="hasWizardResults"
      ref="shownResults"
      :assessments="shownWizardResults"
      is-from-wizard
    />
    <template v-else-if="!hasWizardSearch">
      <div v-if="hasFailed" role="alert" class="mb-4 rounded-xs bg-red-100 p-3 text-text-md text-red-400">
        Something went wrong while searching. Please reload the page to try again.
      </div>
      <p v-else-if="isLoading" role="status" class="text-text-md text-gray-700">
        <span class="material-icons-round animate-spin align-middle text-[1em]" aria-hidden="true">autorenew</span>
        Loading...
      </p>
      <template v-else>
        <p v-if="hasCriteria" class="pb-4 text-sm text-gray-700" aria-hidden="true" data-testid="result-count">{{ resultCountText }}</p>
        <p v-if="!hits.length" class="mb-4 border border-blue-300 bg-blue-200 py-2.5 pl-4 pr-9 text-text-md text-blue-500">
          Sorry, we currently don't have any assessments matching your query; try broadening your search by de-selecting some criteria above.
        </p>
        <ul v-else-if="!hasCriteria" class="list-none">
          <li v-for="tip in EMPTY_STATE_TIPS" :key="tip" class="mb-14 mt-6 text-[25px] leading-10 text-gray-800">{{ tip }}</li>
        </ul>
      </template>
      <span class="sr-only" role="status" aria-live="polite" data-testid="result-count-status">{{ announcedResultCountText }}</span>
      <BrowseAssessmentsResults
        v-if="isAlgoliaResultListMounted"
        v-show="isShowingAlgoliaResults"
        ref="shownResults"
        :assessments="hits"
        :algolia-query-id="queryID"
        :algolia-index="algoliaIndex"
      />
    </template>
  </BrowseLayout>
</template>
